import { catalog, type SkillId } from '@wyn/content';
import type { PlayerState } from './player.ts';
import { renownProgress, skillProgress } from './progression.ts';

/** Renown, all coin held (purse, bank and locked), and each skill by total XP. */
export type BoardId = 'renown' | 'wealth' | `skill:${SkillId}`;

export const BOARDS: BoardId[] = ['renown', 'wealth', ...catalog.skills.map((s) => `skill:${s.id}` as const)];

export const isBoard = (x: string): x is BoardId => (BOARDS as string[]).includes(x);

export const LEADERBOARD = {
  /** Write shards per board, so no one partition takes every score change. */
  shards: 10,
  /** Places shown on a board. */
  top: 50,
};

export const wealthOf = (p: Pick<PlayerState, 'purse' | 'bank' | 'locked'>) => p.purse + p.bank + (p.locked ?? 0);

/** The scores a player row carries. A skill's score is on its own row (`skillBoard`). */
export const profileScores = (p: Pick<PlayerState, 'renown' | 'purse' | 'bank' | 'locked'>): [BoardId, number][] => [
  ['renown', p.renown],
  ['wealth', wealthOf(p)],
];

export const skillBoard = (skill: string): BoardId | null => {
  const id = `skill:${skill}`;
  return isBoard(id) ? id : null;
};

/** The level a score stands for: player level for Renown, the skill's level for a skill, none for wealth. */
export function boardLevel(board: BoardId, score: number): number | null {
  if (board === 'renown') return renownProgress(score).level;
  if (board === 'wealth') return null;
  return skillProgress(score).level;
}

/** Which write shard a player's rows live in. Stable for a player, spread evenly across players (FNV-1a). */
export function boardShard(playerId: string, shards = LEADERBOARD.shards): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < playerId.length; i++) {
    h ^= playerId.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) % shards;
}

export interface BoardEntry { playerId: string; score: number }

/**
 * The top `n` across shards, each already sorted best first. Ties go to the lower player id, so the
 * order is the same on every read.
 */
export function mergeShards(shards: BoardEntry[][], n: number): BoardEntry[] {
  return shards
    .flat()
    .sort((a, b) => b.score - a.score || (a.playerId < b.playerId ? -1 : a.playerId > b.playerId ? 1 : 0))
    .slice(0, n);
}
